import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { routeLoaded, routeUnloaded } from 'store/modules/app';
import {
	scrollTo,
	scrollToElement,
} from '@sparefoot/react-core/utils/scrolling';
import parseSearchLink from '../utils/search/parseSearchLink';
import {
	parseFacilityId,
	parsePSPKeyword,
	parseMoveInStatus,
} from '../utils/routes/parseParams';

function EnhancedRoute({
	actions: actionsProp,
	loadData,
	ready,
	component: Component,
	...rest
}) {
	const rawParams = useParams();
	const location = useLocation();
	const dispatch = useDispatch();

	// Enrich params: parse derived values for backward compat
	const params = useMemo(() => {
		const enriched = { ...rawParams };
		if (rawParams.facilityPath) {
			enriched.facilityId = parseFacilityId(rawParams.facilityPath);
		}
		if (rawParams.citySlug) {
			const cityFacilityId = parseFacilityId(rawParams.citySlug);
			if (cityFacilityId) {
				enriched.facilityId = cityFacilityId;
			}
		}
		if (rawParams.pspSlug) {
			enriched.keyword = parsePSPKeyword(rawParams.pspSlug);
		}
		if (rawParams.confirmSlug) {
			enriched.moveInStatus = parseMoveInStatus(rawParams.confirmSlug);
		}
		return enriched;
	}, [rawParams]);

	// Build match-like object for backward compat with loadData + selector signatures
	const match = useMemo(
		() => ({ params, url: location.pathname }),
		[params, location.pathname],
	);

	const actions = useMemo(
		() =>
			bindActionCreators(
				{
					...actionsProp,
					routeLoaded,
					routeUnloaded,
				},
				dispatch,
			),
		[actionsProp, dispatch],
	);

	const prevMatchParamsRef = useRef(params);
	const prevLocationSearchRef = useRef(location?.search);
	const prevLocationPathnameRef = useRef(location?.pathname);
	const mountedRef = useRef(false);

	const fetchData = useCallback(() => {
		if (loadData) {
			loadData({
				match,
				locationKey: location.key,
				queryParams: parseSearchLink(location.search),
				actions,
			});
		}
	}, [match, location, loadData, actions]);

	// componentDidMount
	useEffect(() => {
		if (ready && loadData) {
			fetchData();
		}
		actions.routeLoaded(location);
		if (location.hash && ready) {
			scrollToElement(location.hash, { behavior: 'auto' });
		} else if (ready) {
			scrollTo(0, 'auto');
		}
		mountedRef.current = true;

		return () => {
			actions.routeUnloaded(location);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// componentDidUpdate — react to route param / search / pathname changes
	useEffect(() => {
		if (!mountedRef.current) return;

		const paramsChanged = !isEqual(params, prevMatchParamsRef.current);
		const searchCleared =
			prevLocationSearchRef.current && !location?.search;
		const pathnameChanged =
			prevLocationPathnameRef.current !== location?.pathname;
		const searchChanged =
			prevLocationSearchRef.current !== location?.search;

		if (
			paramsChanged ||
			searchCleared ||
			pathnameChanged ||
			searchChanged
		) {
			scrollTo(0, 'auto');
			if (ready && loadData) {
				fetchData();
			}
			if (pathnameChanged) {
				actions.routeLoaded(location);
			}
		}

		prevMatchParamsRef.current = params;
		prevLocationSearchRef.current = location?.search;
		prevLocationPathnameRef.current = location?.pathname;
	}, [params, location, ready, loadData, fetchData, actions]);

	return (
		<Component
			match={match}
			location={location}
			queryParams={parseSearchLink(location.search)}
			{...rest}
		/>
	);
}

EnhancedRoute.propTypes = {
	actions: PropTypes.object,
	loadData: PropTypes.func,
	ready: PropTypes.bool,
	component: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
		.isRequired,
};

export default EnhancedRoute;
